import * as React from "react";

/**
 * useDebouncedAvailabilityCheck — the missing request-lifecycle layer on top of
 * the package's `useDebouncedValue`. Every "is this name / URL free?" field
 * (studio custom URL, workspace name, account username) hand-rolls the same
 * effect: debounce the input, fire one network check, render
 * idle / checking / available / taken, surface a suggestion, and stop a slow
 * earlier response from clobbering a fast later one. This hook owns exactly
 * that, and nothing else (no input sanitization, no rendered copy), so it can
 * back any of those fields regardless of transport.
 *
 * Correctness property (the load-bearing reason this exists): a monotonic
 * request-id is captured before each fire and re-checked after the promise
 * settles, in the resolve, the reject, AND the cleanup paths. A response is
 * dropped if a newer request has started since it was issued, so an out-of-
 * order resolution can never win. An `AbortSignal` is also handed to `check`
 * so transports that honor it (fetch, RTK Query) cancel the in-flight request
 * outright; the id guard is the backstop for transports that do not.
 *
 * Transport-agnostic by design: the async `check` is injected (an RTK Query
 * `.unwrap()` thunk, a plain service call, anything returning a promise), and
 * `getAvailability` maps the repo-specific response shape (`{ available,
 * suggested }` vs `{ available, message }`) onto the common result. No fetch
 * library is pulled in — adding one would fight the data layer the consumers
 * already own.
 *
 * SSR-safe: no `window` / `document` at module load, and the `setTimeout`
 * debounce only ever runs inside an effect.
 *
 * @example
 *   const url = useDebouncedAvailabilityCheck({
 *     value: slug,
 *     delay: 400,
 *     enabled: slug.length > 0,
 *     transform: (v) => v.replace(/^-+|-+$/g, ""),
 *     check: (v, signal) => triggerValidateUrl(v, signal).unwrap(),
 *     getAvailability: (res) => ({ available: res.available, suggestion: res.suggested }),
 *   });
 *   // url.status -> "idle" | "checking" | "available" | "taken" | "error"
 *   // url.available -> boolean | null   (null until the first resolution)
 *   // url.suggestion -> the proposed free value, when taken
 */

/** The lifecycle of a single availability check. */
export type AvailabilityStatus =
  | "idle"
  | "checking"
  | "available"
  | "taken"
  | "error";

/** The common, transport-independent shape a consumer maps its response onto. */
export interface AvailabilityResult {
  /** Whether the checked value is free to use. */
  available: boolean;
  /** A proposed free alternative when the value is taken (e.g. `my-studio-2`). */
  suggestion?: string;
  /** A human-readable note from the backend (e.g. "Name already in use."). */
  message?: string;
}

export interface UseDebouncedAvailabilityCheckOptions<TResult> {
  /** The raw input to watch. Compared and fired only after `delay` of stillness. */
  value: string;
  /**
   * Injected transport. Receives the (optionally transformed) value plus an
   * `AbortSignal` that is aborted when the value changes or the component
   * unmounts. Resolve with the repo-specific response; reject to surface
   * `status: "error"`.
   */
  check: (value: string, signal?: AbortSignal) => Promise<TResult>;
  /** Maps the repo-specific response onto the common `{ available, suggestion?, message? }`. */
  getAvailability: (res: TResult) => AvailabilityResult;
  /** Stillness before firing, in ms. Default 400 (workspace + studio use 400; team settings use 2000). */
  delay?: number;
  /** Below this length the check resets to idle without firing. Default 1. */
  minLength?: number;
  /**
   * The current persisted value. When the (transformed) input equals it, the
   * check resets to idle without firing, so editing a field to its existing
   * value never flags it as taken.
   */
  skipWhenEqualTo?: string;
  /**
   * Set false to suppress the fetch while the input is locally invalid
   * (numeric-only, trailing hyphen, failing a format rule). When false the
   * check resets to idle and no request is issued. Default true.
   */
  enabled?: boolean;
  /** Normalize the value before compare / fire (e.g. trim, strip edge dashes). */
  transform?: (value: string) => string;
}

export interface AvailabilityState<TResult> {
  /** The current lifecycle phase. */
  status: AvailabilityStatus;
  /** The raw response from the last successful `check`, if any. */
  data?: TResult;
  /** Whether the value is free. `null` until the first resolution (matches `useReducedMotion`). */
  available: boolean | null;
  /** The proposed free alternative when taken, mapped via `getAvailability`. */
  suggestion?: string;
  /** The backend note, mapped via `getAvailability`. */
  message?: string;
  /** Force the machine back to idle and cancel any in-flight check. */
  reset: () => void;
}

export function useDebouncedAvailabilityCheck<TResult>(
  options: UseDebouncedAvailabilityCheckOptions<TResult>,
): AvailabilityState<TResult> {
  const {
    value,
    check,
    getAvailability,
    delay = 400,
    minLength = 1,
    skipWhenEqualTo,
    enabled = true,
    transform,
  } = options;

  const [status, setStatus] = React.useState<AvailabilityStatus>("idle");
  const [data, setData] = React.useState<TResult | undefined>(undefined);
  const [available, setAvailable] = React.useState<boolean | null>(null);
  const [suggestion, setSuggestion] = React.useState<string | undefined>(undefined);
  const [message, setMessage] = React.useState<string | undefined>(undefined);

  // Monotonic request id — captured before each fire, re-checked after every
  // settle. This is the out-of-order guard. A ref (not state) so incrementing
  // it never triggers a render and the comparison reads the latest value.
  const requestRef = React.useRef(0);

  // Keep the injected callbacks in refs so the debounce effect can depend only
  // on the inputs that should re-trigger a check (value / delay / gates) and
  // not re-fire every render just because the consumer passed inline closures.
  const checkRef = React.useRef(check);
  const getAvailabilityRef = React.useRef(getAvailability);
  React.useEffect(() => {
    checkRef.current = check;
    getAvailabilityRef.current = getAvailability;
  });

  const resetState = React.useCallback(() => {
    setStatus("idle");
    setData(undefined);
    setAvailable(null);
    setSuggestion(undefined);
    setMessage(undefined);
  }, []);

  const reset = React.useCallback(() => {
    // Invalidate any in-flight request so its settle is ignored, then clear.
    requestRef.current += 1;
    resetState();
  }, [resetState]);

  const normalized = transform ? transform(value) : value;

  React.useEffect(() => {
    // Gates that mean "do not check" — bump the id so a request already in
    // flight is disowned, reset to idle, and fire nothing.
    if (
      !enabled ||
      normalized.length < minLength ||
      (skipWhenEqualTo !== undefined && normalized === skipWhenEqualTo)
    ) {
      requestRef.current += 1;
      resetState();
      return;
    }

    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : undefined;

    setStatus("checking");

    const timer = setTimeout(() => {
      const requestId = (requestRef.current += 1);
      Promise.resolve(checkRef.current(normalized, controller?.signal))
        .then((res) => {
          if (requestId !== requestRef.current) return;
          const mapped = getAvailabilityRef.current(res);
          setData(res);
          setAvailable(mapped.available);
          setSuggestion(mapped.suggestion);
          setMessage(mapped.message);
          setStatus(mapped.available ? "available" : "taken");
        })
        .catch((err: unknown) => {
          if (requestId !== requestRef.current) return;
          // An aborted request is expected churn, not a real failure — its
          // successor is already in flight, so leave the machine alone.
          if (controller?.signal.aborted) return;
          if (err instanceof DOMException && err.name === "AbortError") return;
          setStatus("error");
          setAvailable(null);
          setSuggestion(undefined);
          setMessage(undefined);
        });
    }, delay);

    return () => {
      clearTimeout(timer);
      controller?.abort();
    };
  }, [normalized, delay, minLength, skipWhenEqualTo, enabled, resetState]);

  return { status, data, available, suggestion, message, reset };
}
